import React, { useMemo, useState } from "react";
import { Navigate } from "react-router-dom";
import { LoaderCircle } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Column, DataTable } from "@/components/ui/data-table";
import { Button } from "@/components/ui/button";
import { HotelSubscriptionDatesCell } from "@/components/pages/hotels/components/HotelSubscriptionDatesCell";
import { HotelSubscriptionPlanCell } from "@/components/pages/hotels/components/HotelSubscriptionPlanCell";
import { HotelSubscriptionPlansDialog } from "@/components/pages/hotels/components/HotelSubscriptionPlansDialog";
import {
  useHotelSubscription,
  useSubscriptionPortalUrl,
  useTenantSubscriptionPlans,
} from "@/components/pages/hotels/hooks/useHotelSubscriptions";
import { useHotels } from "@/components/pages/hotels/hooks/useHotels";
import { useAuthenticatedUser } from "@/hooks/useAuthenticatedUser";
import { useSorting } from "@/hooks/useSorting";
import type { Hotel as HotelBrand } from "@/types/hotel";
import type { SubscriptionPlan } from "@/types/subscription-plan";
import { isTenantAdminRole } from "@/utils/accessControl";
import { toast } from "sonner";
import { useAccessCapabilities } from "@/hooks/useAccessCapabilities";

const PER_PAGE = 10;

function formatMoney(amount: number | string, currency: string): string {
  const raw =
    typeof amount === "number" ? amount : Number.parseFloat(String(amount));
  const normalizedAmount = Number.isFinite(raw) ? raw : 0;
  const normalizedCurrency =
    currency.trim() === "" ? "USD" : currency.trim().toUpperCase();

  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: normalizedCurrency,
      maximumFractionDigits: normalizedAmount % 1 === 0 ? 0 : 2,
    }).format(normalizedAmount);
  } catch {
    return `${normalizedAmount} ${normalizedCurrency}`;
  }
}

function parseFiniteNumber(value: unknown): number | null {
  if (typeof value === "number") {
    return Number.isFinite(value) ? value : null;
  }

  if (typeof value === "string") {
    const parsed = Number.parseFloat(value.trim());
    return Number.isFinite(parsed) ? parsed : null;
  }

  return null;
}

function normalizeBillingQuantity(value: unknown): number {
  if (typeof value !== "number" || !Number.isFinite(value)) {
    return 1;
  }

  return Math.max(1, Math.trunc(value));
}

interface HotelSubscriptionAmountCellProps {
  hotelId: string;
  plans: SubscriptionPlan[];
}

const HotelSubscriptionAmountCell: React.FC<
  HotelSubscriptionAmountCellProps
> = ({ hotelId, plans }) => {
  const { data: subscription, isLoading } = useHotelSubscription(hotelId, true);

  if (isLoading) {
    return (
      <span className="text-muted-foreground inline-flex items-center justify-center">
        <LoaderCircle className="size-4 animate-spin" />
      </span>
    );
  }

  if (!subscription) {
    return <span className="text-muted-foreground text-sm">—</span>;
  }

  const plan =
    subscription.plan ?? plans.find((item) => item.id === subscription.planId);

  if (!plan) {
    return <span className="text-muted-foreground text-sm">—</span>;
  }

  const perRoomPrice = parseFiniteNumber(plan.price);
  const billingQuantity = normalizeBillingQuantity(
    subscription.billingQuantity,
  );
  const totalAmount =
    perRoomPrice === null ? null : perRoomPrice * billingQuantity;

  return (
    <span className="block text-sm leading-5 font-medium whitespace-nowrap text-zinc-900">
      {totalAmount === null ? "—" : formatMoney(totalAmount, plan.currency)}
    </span>
  );
};

export const BillingSubscriptionsPage = (): React.JSX.Element => {
  const { t } = useTranslation();
  const { userProfile, isLoading: isUserLoading } = useAuthenticatedUser();
  const { capabilities } = useAccessCapabilities();
  const isTenantAdmin = isTenantAdminRole(userProfile);
  const canManageBilling = capabilities.canManageBilling;
  const queryEnabled = !isUserLoading && isTenantAdmin;
  const { sortField, sortDirection, handleSort } = useSorting();
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedHotel, setSelectedHotel] = useState<HotelBrand | null>(null);

  const { data: subscriptionPlans = [] } =
    useTenantSubscriptionPlans(queryEnabled);
  const { mutateAsync: getPortalUrl, isPending: isOpeningBillingPortal } =
    useSubscriptionPortalUrl();
  const { data: hotelsResponse, isLoading: isHotelsLoading } = useHotels(
    {
      currentPage,
      perPage: PER_PAGE,
      sortField,
      sortDirection,
    },
    queryEnabled,
  );

  const hotels = hotelsResponse?.data ?? [];
  const totalHotels = hotelsResponse?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(totalHotels / PER_PAGE));

  const columns: Column<HotelBrand>[] = useMemo(
    () => [
      {
        key: "name",
        header: t("billing.subscriptionsOverview.table.hotel"),
        sortable: true,
        className: "min-w-[220px]",
        render: (_: unknown, row) => (
          <div className="min-w-0">
            <p className="truncate font-medium text-zinc-950">{row.name}</p>
            <p className="text-muted-foreground truncate text-xs">
              {row.hotelAddress?.fullAddress ||
                t("billing.hotelAddressUnavailable")}
            </p>
          </div>
        ),
      },
      {
        key: "subscriptionPlan",
        header: t("billing.subscriptionsOverview.table.plan"),
        className: "min-w-[220px] text-center",
        render: (_: unknown, row) => (
          <HotelSubscriptionPlanCell
            hotelId={row.id}
            plans={subscriptionPlans}
            canManage={canManageBilling}
          />
        ),
      },
      {
        key: "subscriptionPeriod",
        header: t("billing.subscriptionsOverview.table.period"),
        className: "min-w-[180px] text-center",
        render: (_: unknown, row) => (
          <HotelSubscriptionDatesCell hotelId={row.id} type="period" />
        ),
      },
      {
        key: "subscriptionNextPaymentDate",
        header: t("billing.subscriptionsOverview.table.nextPaymentDate"),
        className: "min-w-[160px] text-center",
        render: (_: unknown, row) => (
          <HotelSubscriptionDatesCell hotelId={row.id} type="nextPayment" />
        ),
      },
      {
        key: "subscriptionNextPaymentAmount",
        header: t("billing.subscriptionsOverview.table.nextPaymentAmount"),
        className: "min-w-[160px] text-right",
        render: (_: unknown, row) => (
          <HotelSubscriptionAmountCell
            hotelId={row.id}
            plans={subscriptionPlans}
          />
        ),
      },
      ...(canManageBilling
        ? [
            {
              key: "actions",
              header: t("billing.subscriptionsOverview.table.actions"),
              className: "text-center",
              render: (_: unknown, row: HotelBrand) => (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setSelectedHotel(row)}
                >
                  {t("billing.subscriptionsOverview.table.manageSubscription")}
                </Button>
              ),
            },
          ]
        : []),
    ],
    [canManageBilling, subscriptionPlans, t],
  );

  if (!isUserLoading && !isTenantAdmin) {
    return <Navigate to="/dashboard" replace />;
  }

  const handleManagePaymentMethods = async () => {
    try {
      const response = await getPortalUrl(window.location.href);

      if (!response.url) {
        toast.error(
          t("billing.subscriptionsOverview.portalUnavailable", {
            defaultValue: "Billing portal is unavailable right now.",
          }),
        );
        return;
      }

      window.location.assign(response.url);
    } catch {
      toast.error(
        t("billing.subscriptionsOverview.portalError", {
          defaultValue: "Failed to open billing portal.",
        }),
      );
    }
  };

  return (
    <div className="mx-auto flex max-w-[1344px] flex-col gap-6 p-12">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div className="flex flex-col gap-2">
          <h1 className="text-2xl font-semibold text-zinc-950">
            {t("billing.subscriptionsOverview.title")}
          </h1>
          <p className="text-muted-foreground max-w-3xl text-sm">
            {t("billing.subscriptionsOverview.description")}
          </p>
          <p className="text-muted-foreground max-w-3xl text-sm">
            {t("billing.subscriptionsOverview.paymentMethodsHint", {
              defaultValue:
                "Subscription payment methods are managed in the Stripe billing portal.",
            })}
          </p>
        </div>

        {canManageBilling ? (
          <Button
            type="button"
            variant="outline"
            onClick={handleManagePaymentMethods}
            disabled={isOpeningBillingPortal}
          >
            {isOpeningBillingPortal ? (
              <LoaderCircle className="size-4 animate-spin" />
            ) : null}
            {t("billing.subscriptionsOverview.managePaymentMethods", {
              defaultValue: "Manage payment methods",
            })}
          </Button>
        ) : null}
      </div>

      <p className="text-muted-foreground text-sm">
        {t("billing.subscriptionsOverview.summary.total", {
          count: totalHotels,
        })}
      </p>

      <DataTable<HotelBrand>
        data={hotels}
        columns={columns}
        loading={isHotelsLoading}
        pagination={{
          currentPage,
          totalPages,
          onPageChange: setCurrentPage,
        }}
        sorting={{
          field: sortField,
          direction: sortDirection,
          onSort: handleSort,
        }}
        emptyMessage={t("billing.subscriptionsOverview.table.empty")}
      />

      <HotelSubscriptionPlansDialog
        hotel={selectedHotel}
        canManage={canManageBilling}
        onClose={() => setSelectedHotel(null)}
      />
    </div>
  );
};
